/**
 * 判断传入的值是否为空
 * 支持类型：null、undefined、string、array、object
 */
export function isEmpty(value: any): boolean {
    if (value === null || value === undefined) {
        return true;
    }

    if (typeof value === 'string') {
        return value.trim() === '';
    }

    if (Array.isArray(value)) {
        return value.length === 0;
    }

    if (typeof value === 'object') {
        return Object.keys(value).length === 0;
    }

    return false;
}

/**
 * 判断传入的值是否不为空
 * 支持类型：null、undefined、string、array、object
 */
export function isNotEmpty(value: any): boolean {
    return !isEmpty(value);
}

/**
 * 输入字符串和截取的长度，使其保留长度内的内容，超出的用...号代替
 */
export function truncateString(str: string, length: number): string {
    if( !str ){
        return ''
    }
    if (str.length <= length) {
        return str;
    }
    return str.substring(0, length) + '...';
}

/**
 * 方法： 传入金额和单位，将其转为对应的单位显示
 * @param amount 金额数值
 * @param unit 单位（'百' | '千' | '万'）
 */
export function formatAmountWithUnit(amount: number, unit: '百' | '千' | '万'): string {
    switch (unit) {
        case '万':
            return (amount / 10000).toFixed(2) + '万';
        case '千':
            return (amount / 1000).toFixed(2) + '千';
        case '百':
            return (amount / 100).toFixed(2) + '百';
        default:
            return amount.toString();
    }
}

/**
 * 方法： 传入字符串金额，将其转为正常金额
 */
export function parseAmountString(amountStr: string): number {
    if (!amountStr) {
        return 0;
    }
    
    // 移除空格和可能的货币符号
    let cleanStr = amountStr.replace(/[^\d.-]/g, '');
    
    // 处理包含单位的情况
    if (amountStr.includes('万')) {
        const num = parseFloat(cleanStr);
        return isNaN(num) ? 0 : num * 10000;
    } else if (amountStr.includes('千')) {
        const num = parseFloat(cleanStr);
        return isNaN(num) ? 0 : num * 1000;
    } else if (amountStr.includes('百')) {
        const num = parseFloat(cleanStr);
        return isNaN(num) ? 0 : num * 100;
    }
    
    const result = parseFloat(cleanStr);
    return isNaN(result) ? 0 : result;
}

/**
 * 方法：根据时间字符串，然后根据现在的时候，去判断传入的时间距离现在是多久，如一分钟，几分钟，几小时，昨天，前天，三天前，一周前，超过一周的时间不做处理，直接返回原格式
 * @param timeStr 时间字符串
 * @returns 格式化后的时间描述
 */
export function formatTimeAgo(timeStr: string): string {
    if (!timeStr) {
        return '';
    }
    
    // 缓存当前时间，避免重复创建
    const now = Date.now();
    const targetTime = new Date(timeStr).getTime();
    
    // 如果时间无效，返回原字符串
    if (isNaN(targetTime)) {
        return timeStr;
    }
    
    const diffMs = now - targetTime;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) {
        return '刚刚';
    } else if (diffMinutes < 60) {
        return `${diffMinutes}分钟前`;
    } else if (diffHours < 24) {
        return `${diffHours}小时前`;
    } else if (diffDays === 1) {
        return '昨天';
    } else if (diffDays === 2) {
        return '前天';
    } else if (diffDays <= 7) {
        return `${diffDays}天前`;
    } else {
        // 超过一周，返回原格式
        return timeStr;
    }
}

/**
 * 方法 传入时间字符串，只显示年月日，如2025-12-08
 */
export function formatDateToYMD(dateStr: string): string {
    if (!dateStr) {
        return '';
    }
    
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
        return dateStr; // 如果日期无效，返回原始字符串
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // 月份从0开始，需要+1
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
}

/**
 * 传入任意类型的值，深度拷贝一份，返回
 */
export function deepClone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}

/**
 * 生成uuid字符串
 */
export function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * 把字节数 B 自动格式化为最合适的单位 B/KB/MB/GB/TB
 * @param {number} bytes - 传入 long 类型，单位 B
 * @returns {string} 格式化后的大小，例如 1024 → 1.00 KB
 */
export function formatFileSize(bytes) {
    if( bytes == '不限量'){
        return '不限量';
    }
  // 空值/0 直接返回
  if (bytes === 0 || bytes == null || bytes == '0') return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.floor(Math.log(bytes) / Math.log(1024));

  // 防止超出范围
  const unitIndex = Math.min(index, units.length - 1);

  // 计算大小
  const size = bytes / Math.pow(1024, unitIndex);

  // 保留 2 位小数（最小单位 B 不处理小数）
  return unitIndex === 0
    ? `${size} ${units[unitIndex]}`
    : `${size.toFixed(2)} ${units[unitIndex]}`;
}